import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FlexBox } from '../box';
import { getIcon, IconUploadFile } from '../icon';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { Typography } from '../typography';
import { deepMerge } from '../utils';
import { FilePreview } from './file-preview';
import { buildAcceptObject, formatBytes } from './helpers';
import { useErrors, useFiles } from './hooks';
export { formatBytes, toExtendedFile };
var FileUploaderStates;
(function (FileUploaderStates) {
    FileUploaderStates[FileUploaderStates["DEFAULT"] = 0] = "DEFAULT";
    FileUploaderStates[FileUploaderStates["LIMIT_REACHED"] = 1] = "LIMIT_REACHED";
    FileUploaderStates[FileUploaderStates["LIMIT_EXCEEDED"] = 2] = "LIMIT_EXCEEDED";
})(FileUploaderStates || (FileUploaderStates = {}));
const getDefaultTranslations = () => ({
    uploader: {
        title: {
            click: 'Click to upload',
            drag: 'or drag and drop files here'
        },
        info: (maxFiles, maxSize) => `You can upload ${maxFiles} ${maxFiles > 1 ? 'files' : 'file'}. (max ${maxSize}MB)`,
        reachLimitInfo: (maxFiles) => `Upload limit reached. Max ${maxFiles} ${maxFiles > 1 ? 'files' : 'file'} allowed.`,
        exceedLimitInfo: (maxFiles) => `Upload limit exceeded (${maxFiles} ${maxFiles > 1 ? 'files' : 'file'}). Please remove a file to proceed.`
    },
    errors: {
        invalidFileType: () => 'File type not supported.',
        fileTooLarge: (maxSize) => `File is larger than ${maxSize} MB`,
        tooManyFiles: (maxFiles) => `Upload limit exceeded. Max ${maxFiles} ${maxFiles > 1 ? 'files' : 'file'} allowed.`
    }
});
const createFileError = (file, type, message) => ({
    formattedSize: formatBytes(file.size),
    message,
    file,
    type
});
const toExtendedFile = (file) => Object.assign(file, {
    preview: URL.createObjectURL(file),
    formattedSize: formatBytes(file.size)
});
const isDuplicateFile = (file, existingFiles) => {
    return existingFiles.some((existingFile) => existingFile.name === file.name);
};
const getDropzoneStyles = (theme, { $disabled, $isDragActive, $fileLimitExceeded }) => {
    if ($fileLimitExceeded) {
        return {
            backgroundColor: theme.palette.components?.alert?.error?.background,
            borderColor: theme.palette.error.main,
            borderStyle: 'solid',
            cursor: 'not-allowed'
        };
    }
    if ($disabled) {
        return {
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.elevation?.outlined,
            cursor: 'not-allowed'
        };
    }
    if ($isDragActive) {
        return {
            backgroundColor: theme.palette.primary._states.selected,
            borderColor: theme.palette.primary._states['outlined-border'],
            cursor: 'pointer'
        };
    }
    return {
        backgroundColor: theme.palette.background.default,
        borderColor: theme.palette.elevation?.outlined,
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.primary._states['outlined-border']
        }
    };
};
const DropzoneContainer = styled(FlexBox)(({ theme, $isDragActive, $disabled, $fileLimitExceeded }) => ({
    height: '150px',
    borderRadius: theme.shape.borderRadius,
    transition: 'all .25s ease-in-out',
    position: 'relative',
    borderWidth: '1px',
    borderStyle: 'dashed',
    ...getDropzoneStyles(theme, {
        $disabled,
        $isDragActive,
        $fileLimitExceeded
    })
}));
const ClickableText = styled(Typography)(() => ({
    textDecoration: 'underline'
}));
const getUploaderColors = (disabled) => {
    if (disabled) {
        return {
            icon: 'text.disabled',
            primary: 'text.disabled',
            secondary: 'text.disabled',
            clickable: 'text.disabled'
        };
    }
    return {
        icon: 'primary',
        primary: 'text.secondary',
        secondary: 'text.secondary',
        clickable: 'primary'
    };
};
const FileUploaderStateContent = ({ iconType, message, color }) => {
    const IconComponent = getIcon(iconType);
    return (_jsxs(_Fragment, { children: [_jsx(IconComponent, { fontSize: "small", color: color }), _jsx(Typography, { color: color, children: message })] }));
};
const FileUploaderDefaultContent = ({ disabled, clickMessage, dragMessage, infoMessage }) => {
    const colors = getUploaderColors(disabled);
    return (_jsxs(_Fragment, { children: [_jsx(IconUploadFile, { fontSize: "small", color: colors.icon }), _jsxs(Typography, { color: colors.primary, textAlign: "center", children: [_jsx(ClickableText, { component: "span", variant: "headingSmall", color: colors.clickable, children: clickMessage }), dragMessage && _jsx(_Fragment, { children: ` ${dragMessage}` })] }), _jsx(Typography, { color: colors.secondary, children: infoMessage })] }));
};
const getUploaderState = (isLimitExceeded, isLimitReached) => {
    if (isLimitExceeded) {
        return FileUploaderStates.LIMIT_EXCEEDED;
    }
    if (isLimitReached) {
        return FileUploaderStates.LIMIT_REACHED;
    }
    return FileUploaderStates.DEFAULT;
};
const FileUploaderContent = (props) => {
    const { isLimitExceeded, isLimitReached, disabled, clickMessage, dragMessage, infoMessage, limitReachedMessage, limitExceededMessage } = props;
    const state = getUploaderState(isLimitExceeded, isLimitReached);
    switch (state) {
        case FileUploaderStates.LIMIT_EXCEEDED:
            return (_jsx(FileUploaderStateContent, { iconType: "IconInfo", message: limitExceededMessage || '', color: "error.main" }));
        case FileUploaderStates.LIMIT_REACHED:
            return (_jsx(FileUploaderStateContent, { iconType: "IconWarning", message: limitReachedMessage || '', color: "text.disabled" }));
        case FileUploaderStates.DEFAULT:
        default:
            return (_jsx(FileUploaderDefaultContent, { disabled: disabled, clickMessage: clickMessage, dragMessage: dragMessage, infoMessage: infoMessage }));
    }
};
const FileUploader = ({ maxFiles = 5, maxSize = 3, disabled = false, disableDrag = false, skeleton = false, translations: externalTranslations, allowedFileTypes, files: externalFiles, onFilesChange, errors: externalErrors, onErrorsChange }) => {
    if (maxFiles < 1) {
        throw new Error('[FileUploader] maxFiles must be greater than 0');
    }
    if (maxSize < 1) {
        throw new Error('[FileUploader] maxSize must be greater than 0');
    }
    const { files, addFiles, removeFile } = useFiles(externalFiles, onFilesChange);
    const { errors, addErrors, removeError } = useErrors(externalErrors, onErrorsChange);
    const translations = deepMerge(getDefaultTranslations(), externalTranslations ?? {});
    const maxSizeInBytes = maxSize * 1024 * 1024;
    const isSingleFileMode = maxFiles === 1;
    const isLimitReached = files.length === maxFiles;
    const isLimitExceeded = isLimitReached && errors.length > 0 && errors.at(-1)?.type === 'limit';
    const onDrop = (acceptedFiles, fileRejections) => {
        const nonDuplicateFiles = acceptedFiles.filter((file) => !isDuplicateFile(file, files));
        const createRejectedFileErrors = (code, type, getMessage) => {
            return fileRejections
                .filter((rej) => rej.errors.some((e) => e.code === code))
                .map((rej) => createFileError(rej.file, type, getMessage(rej)));
        };
        const invalidTypeErrors = createRejectedFileErrors('file-invalid-type', 'invalidType', () => translations.errors?.invalidFileType?.() ?? '');
        const sizeErrors = createRejectedFileErrors('file-too-large', 'size', () => translations.errors?.fileTooLarge?.(maxSize) ?? '');
        if (isSingleFileMode && nonDuplicateFiles.length === 1) {
            const newFile = toExtendedFile(nonDuplicateFiles[0]);
            if (files.length > 0) {
                removeFile(0, newFile);
            }
            else {
                addFiles([newFile]);
            }
            addErrors([...invalidTypeErrors, ...sizeErrors]);
            return;
        }
        const availableSlots = Math.max(0, maxFiles - files.length);
        const newErrors = [
            ...invalidTypeErrors,
            ...sizeErrors,
            ...nonDuplicateFiles
                .slice(availableSlots)
                .map((file) => createFileError(file, 'limit', translations?.errors?.tooManyFiles?.(maxFiles) ?? ''))
        ];
        const newValidFiles = nonDuplicateFiles
            .slice(0, availableSlots)
            .map(toExtendedFile);
        addFiles(newValidFiles);
        addErrors(newErrors);
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxSize: maxSizeInBytes,
        disabled: (!isSingleFileMode && (isLimitReached || isLimitExceeded)) ||
            disabled,
        noDrag: disableDrag,
        accept: buildAcceptObject(allowedFileTypes),
        multiple: maxFiles > 1
    });
    const handleRemoveItem = useCallback((itemIndex) => {
        if (itemIndex < files.length) {
            const limitErrors = errors.filter((error) => error.type === 'limit' && error.file);
            if (limitErrors.length > 0) {
                const [errorToPromote] = limitErrors;
                const errorIndex = errors.findIndex((error) => error === errorToPromote);
                if (errorToPromote.file) {
                    const promotedFile = Object.assign(errorToPromote.file, {
                        preview: URL.createObjectURL(errorToPromote.file),
                        formattedSize: errorToPromote.formattedSize
                    });
                    removeFile(itemIndex, promotedFile);
                    removeError(errorIndex);
                }
            }
            else {
                removeFile(itemIndex);
            }
        }
        else {
            const errorIndex = itemIndex - files.length;
            removeError(errorIndex);
        }
    }, [files, errors, removeFile, removeError]);
    useEffect(() => {
        return () => {
            files.forEach((file) => URL.revokeObjectURL(file.preview));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsx(FlexBox, { stack: true, gap: 0, children: skeleton ? (_jsx(Skeleton, { variant: "rectangular", width: "100%", height: 150 })) : (_jsxs(_Fragment, { children: [_jsxs(DropzoneContainer, { position: "relative", ...getRootProps(), "$isDragActive": isDragActive, "$disabled": disabled || (!isSingleFileMode && isLimitReached), "$fileLimitExceeded": !isSingleFileMode && isLimitExceeded, stack: true, hAlign: "center", vAlign: "center", gap: 1, padding: 3, children: [_jsx("input", { ...getInputProps() }), _jsx(FileUploaderContent, { isLimitExceeded: !isSingleFileMode && isLimitExceeded, isLimitReached: !isSingleFileMode && isLimitReached, disabled: disabled, clickMessage: translations?.uploader?.title?.click, dragMessage: !disableDrag
                                ? translations?.uploader?.title?.drag
                                : undefined, infoMessage: translations?.uploader?.info?.(maxFiles, maxSize), limitReachedMessage: translations?.uploader?.reachLimitInfo?.(maxFiles), limitExceededMessage: translations?.uploader?.exceedLimitInfo?.(maxFiles) })] }), (files.length > 0 || errors.length > 0) && (_jsx(FilePreview, { files: files, errors: errors, onRemoveItem: handleRemoveItem }))] })) }));
};
export { FileUploader };
